/**
 * Bowie-locked voicing tables. C-major template + per-key transposition.
 *
 * The scope-memo §"Bowie-locked voicing tables (v1 — complete)" is the source of
 * truth. The C-major template is locked pitch-by-pitch; all 11 other keys derive
 * by deterministic transposition with key-aware enharmonic spelling (flat-side
 * for F/Bb/Eb/Ab/Db; sharp-side for G/D/A/E/B/F#).
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §8.2 / §8.4
 * Bowie scope memo: .claude/agent-memory/bowie/project_voice_leading_lab_v1_scope.md
 */

import type { NoteName } from '@tunasong/schemas'
import { splitNote, type ChordPair, type VoiceLeadingKey, type VoicedChord, type VoicingPair } from './voice-leading.js'

export interface TemplateVoicingPair {
  smooth: readonly VoicedChord[]
  awkward: readonly VoicedChord[]
}

const v = (pitches: string[]): VoicedChord => ({ pitches: pitches as NoteName[] })

export const C_MAJOR_VOICING_TEMPLATE: Record<ChordPair, TemplateVoicingPair> = {
  'I-V': {
    smooth: [v(['C3', 'E3', 'G3']), v(['B2', 'D3', 'G3'])],
    awkward: [v(['C3', 'E3', 'G3']), v(['G3', 'B3', 'D4'])],
  },
  'I-IV': {
    smooth: [v(['C3', 'E3', 'G3']), v(['C3', 'F3', 'A3'])],
    awkward: [v(['C3', 'E3', 'G3']), v(['A3', 'C4', 'F4'])],
  },
  'V-I': {
    smooth: [v(['G3', 'B3', 'D4']), v(['C3', 'C4', 'E4'])],
    awkward: [v(['G2', 'B2', 'D3']), v(['E3', 'G3', 'C4'])],
  },
  'IV-V': {
    smooth: [v(['A2', 'C3', 'F3']), v(['B2', 'D3', 'G3'])],
    awkward: [v(['F2', 'A2', 'C3']), v(['G2', 'B2', 'D3'])],
  },
}

export const SEMITONE_OFFSET: Record<VoiceLeadingKey, number> = {
  C: 0, Db: 1, D: 2, Eb: 3, E: 4, F: 5,
  'F#': 6, G: -5, Ab: -4, A: -3, Bb: -2, B: -1,
}

const PITCH_CLASS: Record<string, number> = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5,
  'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11,
}

const SHARP_SPELLING: Record<number, string> = {
  0: 'C', 1: 'C#', 2: 'D', 3: 'D#', 4: 'E', 5: 'F',
  6: 'F#', 7: 'G', 8: 'G#', 9: 'A', 10: 'A#', 11: 'B',
}

const FLAT_SPELLING: Record<number, string> = {
  0: 'C', 1: 'Db', 2: 'D', 3: 'Eb', 4: 'E', 5: 'F',
  6: 'Gb', 7: 'G', 8: 'Ab', 9: 'A', 10: 'Bb', 11: 'B',
}

const FLAT_KEYS: ReadonlySet<VoiceLeadingKey> = new Set<VoiceLeadingKey>(['F', 'Bb', 'Eb', 'Ab', 'Db'])

const transposePitch = (pitch: NoteName, semitones: number, targetKey: VoiceLeadingKey): NoteName => {
  const { pitch: p, octave } = splitNote(pitch)
  const sourceClass = PITCH_CLASS[p]
  const targetClass = (((sourceClass + semitones) % 12) + 12) % 12
  const spelling = FLAT_KEYS.has(targetKey) ? FLAT_SPELLING : SHARP_SPELLING
  const sourceMidi = (octave + 1) * 12 + sourceClass
  const targetMidi = sourceMidi + semitones
  const newOctave = Math.floor(targetMidi / 12) - 1
  return `${spelling[targetClass]}${newOctave}` as NoteName
}

export const transposeVoicing = (
  template: VoicedChord,
  semitones: number,
  targetKey: VoiceLeadingKey = 'C'
): VoicedChord => ({
  pitches: template.pitches.map(p => transposePitch(p, semitones, targetKey)),
})

export const getVoicingPair = (key: VoiceLeadingKey, pair: ChordPair): VoicingPair => {
  const template = C_MAJOR_VOICING_TEMPLATE[pair]
  const semitones = SEMITONE_OFFSET[key]
  return {
    pair,
    key,
    smooth: template.smooth.map(chord => transposeVoicing(chord, semitones, key)),
    awkward: template.awkward.map(chord => transposeVoicing(chord, semitones, key)),
  }
}
